/**
 * DuplicateComparisonModal Component
 * Side-by-side comparison of imported vs existing transaction
 */
import { useState } from 'react';
import Button from '@/components/ui/Button';
import Alert from '@/components/ui/Alert';
import { formatCurrency, formatDate } from '@/utils/format';
import { cn } from '@/utils/cn';
import { useResolveObservation } from '@/hooks/useDocuments';
import { ApiClientError } from '@/services/api';
import type { TransactionObservation } from '@/types/document';

export interface DuplicateComparisonModalProps {
  isOpen: boolean;
  observation: TransactionObservation | null;
  stagedImportId: string;
  onClose: () => void;
  onResolved: () => void;
}

export function DuplicateComparisonModal({
  isOpen,
  observation,
  stagedImportId,
  onClose,
  onResolved,
}: DuplicateComparisonModalProps) {
  const [error, setError] = useState<string | null>(null);
  const [applyToSimilar, setApplyToSimilar] = useState(false);

  const resolveMutation = useResolveObservation();

  const handleResolve = async (action: 'import' | 'skip' | 'keep_both' | 'replace') => {
    if (!observation) return;

    setError(null);

    try {
      await resolveMutation.mutateAsync({
        observationId: observation.id,
        request: {
          action,
          applyToSimilar,
        },
        stagedImportId,
      });
      onResolved();
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.detail);
      } else {
        setError('Failed to resolve duplicate. Please try again.');
      }
    }
  };

  if (!isOpen || !observation) return null;

  const matchedTxn = observation.matchedTransaction;
  const confidence = observation.matchConfidence
    ? Math.round(observation.matchConfidence * 100)
    : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="duplicate-modal-title"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <h2
          id="duplicate-modal-title"
          className="text-lg font-semibold text-gray-900 mb-4"
        >
          Possible Duplicate Found
        </h2>

        {error && (
          <Alert variant="error" className="mb-4" onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Confidence badge */}
        {confidence && (
          <div className="mb-4">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
              {confidence}% match confidence
            </span>
          </div>
        )}

        {/* Comparison */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          {/* Imported transaction */}
          <TransactionCard
            title="Imported Transaction"
            date={observation.observedDate}
            amount={observation.observedAmount}
            description={observation.observedDescription}
            source="Statement Import"
            variant="imported"
          />

          {/* Existing transaction */}
          {matchedTxn && (
            <TransactionCard
              title="Existing Transaction"
              date={matchedTxn.date}
              amount={matchedTxn.amount}
              description={matchedTxn.description}
              source={matchedTxn.source}
              variant="existing"
            />
          )}
        </div>

        {/* Match reasons */}
        {observation.matchReasons.length > 0 && (
          <div className="mb-6 p-3 bg-gray-50 rounded-lg">
            <p className="text-sm font-medium text-gray-700 mb-2">Match reasons:</p>
            <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
              {observation.matchReasons.map((reason, index) => (
                <li key={index}>{reason}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Apply to similar checkbox */}
        <div className="mb-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={applyToSimilar}
              onChange={(e) => setApplyToSimilar(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">
              Apply this decision to similar duplicates
            </span>
          </label>
        </div>

        {/* Actions */}
        <div className="space-y-3">
          <p className="text-sm font-medium text-gray-700">What would you like to do?</p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <ActionButton
              label="Keep Both"
              description="Import as separate transaction"
              onClick={() => handleResolve('keep_both')}
              disabled={resolveMutation.isPending}
            />
            <ActionButton
              label="Skip"
              description="Use existing transaction only"
              onClick={() => handleResolve('skip')}
              disabled={resolveMutation.isPending}
            />
            <ActionButton
              label="Replace"
              description="Replace existing with import"
              onClick={() => handleResolve('replace')}
              disabled={resolveMutation.isPending}
            />
          </div>
        </div>

        {/* Cancel button */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={resolveMutation.isPending}
            className="w-full"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Transaction card for comparison
 */
interface TransactionCardProps {
  title: string;
  date: string;
  amount: number;
  description: string;
  source: string;
  variant: 'imported' | 'existing';
}

function TransactionCard({
  title,
  date,
  amount,
  description,
  source,
  variant,
}: TransactionCardProps) {
  const isNegative = amount < 0;

  return (
    <div
      className={cn(
        'p-4 rounded-lg border',
        variant === 'imported' ? 'border-primary-200 bg-primary-50' : 'border-gray-200 bg-gray-50'
      )}
    >
      <h3 className="text-sm font-medium text-gray-900 mb-3">{title}</h3>

      <dl className="space-y-2 text-sm">
        <div className="flex justify-between">
          <dt className="text-gray-500">Date</dt>
          <dd className="text-gray-900 font-medium">
            {formatDate(date, { month: 'short', day: 'numeric', year: 'numeric' })}
          </dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-gray-500">Amount</dt>
          <dd className={cn('font-medium', isNegative ? 'text-red-600' : 'text-green-600')}>
            {formatCurrency(amount)}
          </dd>
        </div>
        <div>
          <dt className="text-gray-500">Description</dt>
          <dd className="text-gray-900 mt-0.5">{description}</dd>
        </div>
        <div className="flex justify-between pt-2 border-t border-gray-200">
          <dt className="text-gray-500">Source</dt>
          <dd className="text-gray-600 text-xs">{source}</dd>
        </div>
      </dl>
    </div>
  );
}

/**
 * Action button for duplicate resolution
 */
interface ActionButtonProps {
  label: string;
  description: string;
  onClick: () => void;
  disabled: boolean;
}

function ActionButton({ label, description, onClick, disabled }: ActionButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex flex-col items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
    >
      <span className="text-sm font-medium text-gray-900">{label}</span>
      <span className="text-xs text-gray-500 mt-0.5">{description}</span>
    </button>
  );
}

export default DuplicateComparisonModal;
